import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  Popover,
  TextField,
  Stack,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import MainCard from "../components/MainCard";
import { useEffect, useMemo, useState } from "react";
import { getComparator, stableSort } from "../utils";
import EnhancedTableHead from "../components/EnhancedTableHead";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useNavigate, useParams } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import moment from "moment";


const Iteration = () => {
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("executionTime");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const [filters, setFilters] = useState({
    testType: "",
    testEnvironment: "",
    tags: "",
  });
  const [tempFilters, setTempFilters] = useState({
    testType: "",
    testEnvironment: "",
    tags: "",
  });
  const navigate = useNavigate();
  const {
    iterations,
    totalIterations,
    getAllIterations,
    clearIteration,
    setIteration,
    project,
    getProject,
    deleteIteration,
    loggedInUser,
    iteration,
    user,
    getUser,
    clearTestCases,
    organization,
    getOrganization,
    downloadReport,
  } = useAppContext();
  const { organizationId, userId, projectId } = useParams();

  const resetAllFilters = () => {
    setFilters({
      testType: "",
      testEnvironment: "",
      tags: "",
    });
    setPage(0);
  };

  const isFilterApplied = useMemo(() => {
    return !!(filters.testType || filters.testEnvironment || filters.tags);
  }, [filters]);

  const headCells = useMemo(() => [
    {
      id: "label",
      label: "Label",
      align: "left",
      sortable: true,
      width: "10%",
    },
    {
      id: "testType",
      label: "Test Type",
      align: "left",
      sortable: false,
      filterable: true,
      width: "15%",
    },
    {
      id: "testEnvironment",
      label: "Environment",
      align: "left",
      sortable: false,
      filterable: true,
      width: "15%",
    },
    {
      id: "tags",
      label: "Tags",
      align: "left",
      sortable: false,
      filterable: true,
      width: "15%",
    },
    {
      id: "passPercent",
      label: "Passed %",
      align: "center",
      sortable: false,
      width: "7.5%",
    },
    {
      id: "failPercent",
      label: "Fail %",
      align: "center",
      sortable: false,
      width: "7.5%",
    },
    {
      id: "executionTime",
      label: (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          Execution Date
        </Box>
      ),
      align: "center",
      sortable: true,
      width: "15%",
    },
    {
      id: "action",
      label: (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', width: '100%' }}>
          Action
          {isFilterApplied && (
            <Tooltip title="Clear All Filters">
              <IconButton
                size="small"
                sx={{
                  position: 'absolute',
                  right: 0,
                  color: 'error.main',
                  '&:hover': { backgroundColor: 'error.light', color: 'white' }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  resetAllFilters();
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
      align: "center",
      sortable: false,
      width: "15%",
    },
  ], [isFilterApplied]);

  useEffect(() => {
    (async () => {
      if (!organization) await getOrganization(organizationId);
      if (userId !== loggedInUser?._id && !user) await getUser(userId);
      if (!project) await getProject(userId, projectId);
      await getAllIterations(projectId);
    })();
    clearIteration();
  }, []);

  const filterOptions = useMemo(() => {
    const types = new Set();
    const envs = new Set();
    const tags = new Set();

    (iterations || []).forEach(it => {
      if (it.testType) types.add(it.testType);
      if (it.testEnvironment) envs.add(it.testEnvironment);
      if (it.tags) it.tags.forEach(t => tags.add(t));
      if (it.tag) tags.add(it.tag);
    });

    return {
      testType: Array.from(types).sort(),
      testEnvironment: Array.from(envs).sort(),
      tags: Array.from(tags).sort(),
    };
  }, [iterations]);

  const filteredIterations = useMemo(() => {
    let result = iterations || [];

    if (filters.testType) {
      result = result.filter(it =>
        it.testType?.toLowerCase().includes(filters.testType.toLowerCase())
      );
    }

    if (filters.testEnvironment) {
      result = result.filter(it =>
        it.testEnvironment?.toLowerCase().includes(filters.testEnvironment.toLowerCase())
      );
    }

    if (filters.tags) {
      result = result.filter(it =>
        it.tags?.some(tag => tag.toLowerCase().includes(filters.tags.toLowerCase())) ||
        (it.tag && it.tag.toLowerCase().includes(filters.tags.toLowerCase()))
      );
    }

    return result;
  }, [iterations, filters]);

  const visibleRows = useMemo(() => {
    const sortedData = stableSort(filteredIterations || [], (a, b) => {
      if (orderBy === 'executionTime') {
        const getTimeVal = (it) => {
          const val = it.timestamp || it.executionTime;
          if (!val) return 0;
          if (val._seconds !== undefined) return val._seconds * 1000 + (val._nanoseconds || 0) / 1000000;
          if (val.toDate) return val.toDate().getTime();
          const d = new Date(val);
          return isNaN(d.getTime()) ? 0 : d.getTime();
        };
        const valA = getTimeVal(a);
        const valB = getTimeVal(b);
        return order === 'desc' ? valB - valA : valA - valB;
      }
      return getComparator(order, orderBy)(a, b);
    });

    return sortedData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [order, orderBy, page, rowsPerPage, filteredIterations]);

  const handleFilterClick = (event, columnId) => {
    setFilterAnchorEl(event.currentTarget);
    setActiveFilterColumn(columnId);
    setTempFilters((prev) => ({
      ...prev,
      [columnId]: filters[columnId]
    }));
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setActiveFilterColumn(null);
  };

  const handleTempFilterChange = (columnId, value) => {
    setTempFilters((prev) => ({
      ...prev,
      [columnId]: value,
    }));
  };

  const handleApplyFilter = () => {
    setFilters((prev) => ({
      ...prev,
      [activeFilterColumn]: tempFilters[activeFilterColumn]
    }));
    setPage(0);
    handleFilterClose();
  };

  const handleClearColumnFilter = () => {
    setFilters((prev) => ({
      ...prev,
      [activeFilterColumn]: ""
    }));
    setTempFilters((prev) => ({
      ...prev,
      [activeFilterColumn]: ""
    }));
    setPage(0);
    handleFilterClose();
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleRedirection = (event, iteration) => {
    clearTestCases();
    navigate(
      `/testcases/${organizationId}/${userId}/${projectId}/${iteration._id}`
    );
  };

  const handleDownloadReport = async (event, iteration) => {
    await downloadReport(iteration._id);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [modalOpen, setModalOpen] = useState(false);

  const handleDeleteIteration = (event, iteration) => {
    setIteration({ iteration });
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    clearIteration();
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - totalIterations) : 0;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <MainCard
          title={project ? `${project?.projectName}'s Iterations` : " "}
          content={false}></MainCard>
      </Grid>
      <Grid item xs={12}>
        <MainCard>
          {totalIterations > 0 ? (
            <Box sx={{ width: "100%" }}>
              <Paper sx={{ width: "100%", mb: 2 }}>
                <TableContainer>
                  <Table
                    sx={{ minWidth: 750 }}
                    aria-labelledby="tableTitle"
                    size="small">
                    <EnhancedTableHead
                      sortable={true}
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={handleRequestSort}
                      headCells={headCells}
                      onFilterClick={handleFilterClick}
                    />
                    <TableBody>
                      {visibleRows.map((iteration, index) => {
                        let executionStartTime = "-";
                        const timeVal = iteration.timestamp || iteration.executionTime;
                        if (timeVal) {
                          const dateObj = timeVal._seconds
                            ? new Date(timeVal._seconds * 1000)
                            : timeVal;
                          executionStartTime = moment(dateObj).format(
                            "MMM Do YYYY, HH:mm:ss"
                          );
                        }

                        return (
                          <TableRow
                            hover
                            tabIndex={-1}
                            key={iteration._id}
                            sx={{ cursor: "pointer" }}>
                            <TableCell sx={{ width: "10%", wordBreak: "break-all" }}>
                              {iteration.label || iteration.iterationId}
                            </TableCell>
                            <TableCell sx={{ width: "15%" }}>{iteration.testType}</TableCell>
                            <TableCell sx={{ width: "15%", wordBreak: "break-all" }}>
                              {iteration.testEnvironment || "-"}
                            </TableCell>
                            <TableCell align="left" sx={{ width: "15%", wordBreak: "break-all" }}>
                              {iteration.tags?.join(", ") || "-"}
                            </TableCell>
                            <TableCell color="success" align="center" sx={{ width: "7.5%" }}>
                              {(
                                (iteration.passCount /
                                  (iteration.passCount + iteration.failCount ||
                                    1)) *
                                100
                              ).toFixed(2)}
                              %
                            </TableCell>
                            <TableCell color="error" align="center" sx={{ width: "7.5%" }}>
                              {Math.round(
                                (iteration.failCount /
                                  (iteration.passCount + iteration.failCount ||
                                    1)) *
                                100
                              ).toFixed(2)}
                              %
                            </TableCell>
                            <TableCell align="center" sx={{ width: "15%" }}>
                              {executionStartTime}
                            </TableCell>
                            <TableCell align="center" sx={{ width: "15%" }}>
                              {loggedInUser?.role !== "org-member" && (
                                <Tooltip
                                  title="Delete Iteration"
                                  disabled={
                                    loggedInUser?.role === "org-member"
                                  }>
                                  <IconButton
                                    size="large"
                                    color="primary"
                                    tabIndex={-1}
                                    onClick={(event) =>
                                      handleDeleteIteration(event, iteration)
                                    }>
                                    <DeleteOutlinedIcon color="error" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Details">
                                <IconButton
                                  size="large"
                                  color="primary"
                                  tabIndex={-1}
                                  onClick={(event) =>
                                    handleRedirection(event, iteration)
                                  }>
                                  <AssessmentIcon color="primary" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download Report">
                                <IconButton
                                  size="large"
                                  color="secondary"
                                  tabIndex={-1}
                                  onClick={(event) =>
                                    handleDownloadReport(event, iteration)
                                  }>
                                  <CloudDownloadIcon color="secondary" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {emptyRows > 0 && (
                        <TableRow
                          style={{
                            height: 53 * emptyRows,
                          }}>
                          <TableCell colSpan={6} />
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 15, 30]}
                  component="div"
                  count={filteredIterations.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Paper>
            </Box>
          ) : (
            <Typography variant="body1" align="center">
              No Records found.
            </Typography>
          )}

          <Popover
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={handleFilterClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: { p: 2, width: 250 }
            }}
          >
            <Stack spacing={2}>
              <Typography variant="h6">Filter {headCells.find(h => h.id === activeFilterColumn)?.label}</Typography>
              {activeFilterColumn === 'tags' ? (
                <FormControl fullWidth size="small">
                  <InputLabel>Tags</InputLabel>
                  <Select
                    value={tempFilters.tags || ""}
                    label="Tags"
                    onChange={(e) => handleTempFilterChange("tags", e.target.value)}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {filterOptions.tags.map(tag => (
                      <MenuItem key={tag} value={tag}>{tag}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : activeFilterColumn === 'testType' ? (
                <FormControl fullWidth size="small">
                  <InputLabel>Test Type</InputLabel>
                  <Select
                    value={tempFilters.testType || ""}
                    label="Test Type"
                    onChange={(e) => handleTempFilterChange("testType", e.target.value)}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {filterOptions.testType.map(type => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : activeFilterColumn === 'testEnvironment' ? (
                <FormControl fullWidth size="small">
                  <InputLabel>Environment</InputLabel>
                  <Select
                    value={tempFilters.testEnvironment || ""}
                    label="Environment"
                    onChange={(e) => handleTempFilterChange("testEnvironment", e.target.value)}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {filterOptions.testEnvironment.map(env => (
                      <MenuItem key={env} value={env}>{env}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button size="small" color="inherit" onClick={handleClearColumnFilter}>
                  Clear
                </Button>
                <Button size="small" variant="contained" onClick={handleApplyFilter} sx={{ ml: 1 }}>
                  Apply
                </Button>
              </Box>
            </Stack>
          </Popover>
          <Dialog open={modalOpen} onClose={handleClose}>
            <DialogTitle variant="h2">
              Do you want to delete this iteration?
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1">
                Deleting this iteration will delete all the test cases
                associated with it.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                autoFocus
                onClick={async () => {
                  await deleteIteration(projectId, iteration._id);
                  handleClose();
                }}
                color="error">
                Delete
              </Button>
              <Button onClick={handleClose} autoFocus>
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
        </MainCard>
      </Grid>
    </Grid>
  );
};

export default Iteration;
